import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import BitacoraEntry from './BitacoraEntry';
import { useUser } from './UserContext';
import { BITACORA_HIDDEN_USER_IDS } from '../utils/bitacora';

/**
 * BitacoraTimeline Component
 * Displays a timeline of bitácora entries with filters (real-time)
 *
 * @param {Object} filters - Filters to apply
 * @param {string} filters.clientId - Filter by client ID
 * @param {string} filters.contratoId - Filter by contract ID
 * @param {string} filters.linkId - Filter by link ID
 * @param {string} filters.collection - Filter by collection name
 * @param {string} filters.userId - Filter by user ID
 * @param {number} filters.limit - Max number of entries (default: 50)
 * @param {boolean} compact - Use compact view (default: false)
 */
// Mirrors the operator branch of the bitácora read rule in firestore.rules —
// keep both lists in sync or the query fails with permission-denied.
const OPERATOR_ALLOWED_COLLECTIONS = ['contracts', 'links', 'links_files', 'clientes'];

export function BitacoraTimeline({ filters = {}, compact = false, pageSize = 20 }) {
  const { user, userData, isOperator } = useUser();
  const [ownActions, setOwnActions] = useState([]);
  const [orgScoped, setOrgScoped] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(pageSize);

  // Fetch entries with real-time listener(s).
  // Operators can't read all org bitácora — rules restrict them to their own
  // actions OR a fixed collection allowlist. Firestore queries can't express
  // that disjunction, so we run two queries and merge client-side.
  useEffect(() => {
    if (!userData?.org || !user?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const fetchLimit = Math.max(filters.limit || 50, 200);
    const docsFromSnap = (snap) =>
      snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    const handleError = (err) => {
      console.error('Error fetching bitácora:', err);
      setError('Error al cargar el historial');
      setLoading(false);
    };

    const unsubscribers = [];

    if (isOperator) {
      let ownReady = false;
      let orgReady = false;
      const maybeDoneLoading = () => {
        if (ownReady && orgReady) setLoading(false);
      };

      const ownQ = query(
        collection(db, 'bitacora'),
        where('userId', '==', user.uid),
        orderBy('timestamp', 'desc'),
        limit(fetchLimit)
      );
      unsubscribers.push(
        onSnapshot(
          ownQ,
          (snap) => {
            setOwnActions(docsFromSnap(snap));
            ownReady = true;
            maybeDoneLoading();
          },
          handleError
        )
      );

      const orgQ = query(
        collection(db, 'bitacora'),
        where('orgId', '==', userData.org),
        where('collection', 'in', OPERATOR_ALLOWED_COLLECTIONS),
        orderBy('timestamp', 'desc'),
        limit(fetchLimit)
      );
      unsubscribers.push(
        onSnapshot(
          orgQ,
          (snap) => {
            setOrgScoped(docsFromSnap(snap));
            orgReady = true;
            maybeDoneLoading();
          },
          handleError
        )
      );
    } else {
      setOwnActions([]);
      const q = query(
        collection(db, 'bitacora'),
        where('orgId', '==', userData.org),
        orderBy('timestamp', 'desc'),
        limit(fetchLimit)
      );
      unsubscribers.push(
        onSnapshot(
          q,
          (snap) => {
            setOrgScoped(docsFromSnap(snap));
            setLoading(false);
          },
          handleError
        )
      );
    }

    return () => unsubscribers.forEach((u) => u());
  }, [userData?.org, user?.uid, isOperator, filters.limit]);

  const allEntries = useMemo(() => {
    if (!isOperator) return orgScoped;
    const byId = new Map();
    for (const e of ownActions) byId.set(e.id, e);
    for (const e of orgScoped) byId.set(e.id, e);
    return Array.from(byId.values()).sort((a, b) => {
      const ta = a.timestamp?.toMillis?.() ?? 0;
      const tb = b.timestamp?.toMillis?.() ?? 0;
      return tb - ta;
    });
  }, [ownActions, orgScoped, isOperator]);

  // Apply client-side filters
  const filteredEntries = useMemo(() => {
    let filtered = allEntries.filter(
      (entry) => !BITACORA_HIDDEN_USER_IDS.has(entry.userId)
    );

    if (filters.clientId) {
      filtered = filtered.filter(entry => entry.metadata?.clientId === filters.clientId);
    }
    if (filters.convenioId) {
      filtered = filtered.filter(entry => entry.metadata?.convenioId === filters.convenioId);
    }
    if (filters.contratoId) {
      filtered = filtered.filter(entry => entry.metadata?.contratoId === filters.contratoId);
    }
    if (filters.linkId) {
      filtered = filtered.filter(entry => entry.metadata?.linkId === filters.linkId);
    }
    if (filters.collection) {
      filtered = filtered.filter(entry => entry.collection === filters.collection);
    }
    if (filters.userId) {
      filtered = filtered.filter(entry => entry.userId === filters.userId);
    }
    if (filters.action) {
      filtered = filtered.filter(entry => entry.action === filters.action);
    }

    return filtered;
  }, [allEntries, filters]);

  const entries = useMemo(
    () => filteredEntries.slice(0, displayLimit),
    [filteredEntries, displayLimit]
  );

  const hasMore = filteredEntries.length > displayLimit;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">No hay actividad registrada</p>
      </div>
    );
  }

  return (
    <div className={compact ? 'space-y-1' : 'space-y-2'}>
      {/* Timeline line */}
      <div className="relative">
        <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200"></div>

        {/* Entries */}
        <div className="relative">
          {entries.map((entry, index) => (
            <div key={entry.id} className={index !== entries.length - 1 ? 'border-b border-gray-100' : ''}>
              <BitacoraEntry entry={entry} />
            </div>
          ))}
        </div>
      </div>

      {hasMore && (
        <div className="text-center pt-2">
          <button
            onClick={() => setDisplayLimit((prev) => prev + pageSize)}
            className="text-sm text-azul-surelia hover:text-azul-surelia/80 font-medium transition-colors"
          >
            Cargar más ({filteredEntries.length - displayLimit} restantes)
          </button>
        </div>
      )}
    </div>
  );
}

export default BitacoraTimeline;
